import type { CommandData, CommandSnapshot } from '../dev/bridge.js';

/** `ApplicationCommandOptionType`, which the CLI cannot import: it has no dependency on `discord-api-types`. */
const OptionType = {
	Subcommand: 1,
	SubcommandGroup: 2,
	String: 3,
	Integer: 4,
	Boolean: 5,
	User: 6,
	Channel: 7,
	Role: 8,
	Mentionable: 9,
	Number: 10,
	Attachment: 11
} as const;

/** `ApplicationCommandType.ChatInput`: the only type with options, and the one a command without `type` is. */
const CHAT_INPUT = 1;

const IDENTIFIER = /^[A-Za-z_$][\w$]*$/;

interface OptionData {
	name: string;
	type: number;
	required?: boolean;
	choices?: { value: string | number }[];
	channel_types?: number[];
	options?: OptionData[];
}

interface Rendering {
	/** Set once something refers to `TransformedArguments`, which the file then has to import. */
	usesResolvedTypes: boolean;
}

/**
 * The declaration file `stars codegen` writes for the commands a project registers: one `CommandOptionsRegistry` entry
 * (`Command.OptionsOf<'path'>`) per leaf, that is a command without subcommands, `parent sub` or `parent group sub`.
 *
 * It reads the JSON Discord receives rather than the builders, so what the handler is typed with cannot drift from what
 * is registered, and loops and factories need no special care. The option types point at the framework's
 * `TransformedArguments`, which is what the router resolves them to, instead of repeating those shapes here.
 *
 * Context menu commands have no options and are skipped. A path registered more than once (a command restricted to
 * several guilds is the same command in each) keeps its first definition, global commands first. The output depends
 * on nothing but the commands, in sorted order, so `stars codegen --check` is a string comparison.
 */
export function generateCommandTypes(snapshot: CommandSnapshot): string {
	const entries = new Map<string, OptionData[]>();
	const scopes = [
		snapshot.global,
		...Object.keys(snapshot.guilds)
			.sort()
			.map((guild) => snapshot.guilds[guild])
	];
	for (const commands of scopes) {
		for (const command of commands) collectLeaves(command, entries);
	}

	const rendering: Rendering = { usesResolvedTypes: false };
	const lines = [...entries.keys()].sort().map((path) => `    ${renderEntry(path, entries.get(path)!, rendering)}`);

	return [
		'// This file is automatically generated by `stars codegen`, do not edit it.',
		'/* eslint-disable */',
		'/* oxlint-disable */',
		'// oxfmt-ignore',
		'// prettier-ignore',
		rendering.usesResolvedTypes ? "import type { TransformedArguments } from '@wolfstar/http-framework';" : 'export {};',
		'',
		'// oxfmt-ignore',
		'// prettier-ignore',
		"declare module '@wolfstar/http-framework' {",
		'  interface CommandOptionsRegistry {',
		...lines,
		'  }',
		'}',
		''
	].join('\n');
}

function collectLeaves(command: CommandData, entries: Map<string, OptionData[]>): void {
	if ((command.type ?? CHAT_INPUT) !== CHAT_INPUT) return;

	const options = (command.options ?? []) as OptionData[];
	const add = (path: string, leaf: OptionData[] | undefined) => {
		if (!entries.has(path)) entries.set(path, leaf ?? []);
	};

	if (!options.some((option) => option.type === OptionType.Subcommand || option.type === OptionType.SubcommandGroup)) {
		add(command.name, options);
		return;
	}

	for (const option of options) {
		if (option.type === OptionType.Subcommand) {
			add(`${command.name} ${option.name}`, option.options);
		} else if (option.type === OptionType.SubcommandGroup) {
			for (const sub of option.options ?? []) {
				if (sub.type === OptionType.Subcommand) add(`${command.name} ${option.name} ${sub.name}`, sub.options);
			}
		}
	}
}

/** Only the first line of an entry is indented by the caller: the options go on lines of their own. */
function renderEntry(path: string, options: readonly OptionData[], rendering: Rendering): string {
	const sorted = [...options].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
	const properties = sorted.map((option) => `      ${key(option.name)}${option.required ? '' : '?'}: ${renderOption(option, rendering)};`);
	return properties.length === 0 ? `${quote(path)}: {};` : `${quote(path)}: {\n${properties.join('\n')}\n    };`;
}

function renderOption(option: OptionData, rendering: Rendering): string {
	const resolved = (name: string) => {
		rendering.usesResolvedTypes = true;
		return `TransformedArguments.${name}`;
	};

	switch (option.type) {
		case OptionType.String:
		case OptionType.Integer:
		case OptionType.Number: {
			const fallback = option.type === OptionType.String ? 'string' : 'number';
			if (!option.choices?.length) return fallback;
			const values = option.choices.map((choice) => (typeof choice.value === 'string' ? quote(choice.value) : String(choice.value)));
			return [...new Set(values)].join(' | ');
		}
		case OptionType.Boolean:
			return 'boolean';
		case OptionType.User:
			return resolved('User');
		case OptionType.Role:
			return resolved('Role');
		case OptionType.Mentionable:
			return resolved('Mentionable');
		case OptionType.Attachment:
			return resolved('Attachment');
		case OptionType.Channel: {
			const channel = resolved('Channel');
			if (!option.channel_types?.length) return channel;
			// `ChannelType` is not imported on purpose: its members are the numbers they are in the payload.
			const types = [...new Set(option.channel_types)].sort((a, b) => a - b).join(' | ');
			return `${channel} & { type: Extract<${channel}['type'], ${types}> }`;
		}
		default:
			// An option type Discord added after this was written: the handler still gets something.
			return 'unknown';
	}
}

function key(name: string): string {
	return IDENTIFIER.test(name) ? name : quote(name);
}

function quote(value: string): string {
	const escaped = value
		.replaceAll('\\', '\\\\')
		.replaceAll("'", "\\'")
		.replaceAll('\n', '\\n')
		.replaceAll('\r', '\\r')
		.replaceAll(' ', '\\u2028')
		.replaceAll(' ', '\\u2029');
	return `'${escaped}'`;
}
